import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import AppLayout from '../../components/AppLayout';
import { certificateOptions, dayOptions } from '@/mocks/caregivers';
import { REGION_HIERARCHY, ALL_PROVINCES } from '@/mocks/regionData';
import { formatDistance, DEFAULT_USER_LOCATION, searchRegionCoordinates, getRegionCoordinate, getCaregiverDistance } from '@/hooks/distance';
import { useAppAuth } from '@/hooks/useAppAuth';
import { useBookmarks } from '@/hooks/useBookmarks';

type SortMode = 'compound' | 'distance' | 'rating' | 'recommend';

type CaregiverItem = {
  userId: string;
  name: string;
  gender: string;
  age: number;
  avatarUrl: string | null;
  experienceYears: number;
  hourlyRate: number;
  availableRegions: string[];
  certificates: string[];
  introduction: string;
  availableDays: string[];
  availableTimes: string[];
  ratingAvg: number;
  totalMatches: number;
  earliestStartDate: string | null;
  latitude: number;
  longitude: number;
  distance: number;
};

export default function AppPatientSearchPage() {
  const navigate = useNavigate();
  const auth = useAppAuth(false);
  const { isBookmarked, toggleBookmark } = useBookmarks();

  const [allCaregivers, setAllCaregivers] = useState<CaregiverItem[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState('');

  const [searchText, setSearchText] = useState('');
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [expandedProvinces, setExpandedProvinces] = useState<string[]>([]);
  const [regionAllExpanded, setRegionAllExpanded] = useState(false);

  const [showFilters, setShowFilters] = useState(false);
  const [minExp, setMinExp] = useState(0);
  const [maxRate, setMaxRate] = useState(50000);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [selectedCerts, setSelectedCerts] = useState<string[]>([]);

  const [sortMode, setSortMode] = useState<SortMode>('compound');
  const [myLocation, setMyLocation] = useState(DEFAULT_USER_LOCATION);
  const [locationInput, setLocationInput] = useState('');
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [locationFocused, setLocationFocused] = useState(false);

  const [reviewsData, setReviewsData] = useState<Record<string, { count: number; avg: number }>>({});
  const [patientRegion, setPatientRegion] = useState<string>('');
  const [preferredGender, setPreferredGender] = useState<string>('');
  const [recommendedIds, setRecommendedIds] = useState<Set<string>>(new Set());

  const locationSuggestions = useMemo(() => searchRegionCoordinates(locationInput), [locationInput]);

  function locateMe() {
    if (!navigator.geolocation) {
      setLocationError('이 브라우저는 위치 정보를 지원하지 않아요.');
      return;
    }
    setLocating(true);
    setLocationError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setMyLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: '내 현재 위치' });
        setLocationInput('');
        setLocationFocused(false);
        setLocating(false);
      },
      () => {
        setLocationError('위치 권한이 거부되었어요. 지역을 직접 입력해주세요.');
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  }

  function applyLocationInput() {
    const text = locationInput.trim();
    if (!text) return;
    let coord = getRegionCoordinate(text);
    if (!coord) {
      const found = searchRegionCoordinates(text)[0];
      if (found) coord = { lat: found.lat, lng: found.lng };
    }
    if (coord) {
      setMyLocation({ lat: coord.lat, lng: coord.lng, label: text });
      setLocationInput('');
      setLocationFocused(false);
      setLocationError('');
    } else {
      setLocationError('지역을 찾을 수 없어요. (예: 서울 강남구)');
    }
  }

  function selectLocationSuggestion(s: { key: string; lat: number; lng: number }) {
    setMyLocation({ lat: s.lat, lng: s.lng, label: s.key });
    setLocationInput('');
    setLocationFocused(false);
    setLocationError('');
  }

  // Supabase에서 활성 간병인 목록 로드
  useEffect(() => {
    loadCaregivers();
  }, []);

  // 환자 지역 정보 로드 (프로필 + 케어 요청 + 선호 성별)
  useEffect(() => {
    if (!auth.userId) return;
    async function loadPatientPrefs() {
      try {
        // 프로필에서 주소 가져오기
        const { data: prof } = await supabase.from('profiles')
          .select('address')
          .eq('id', auth.userId)
          .maybeSingle();
        if (prof?.address) setPatientRegion(prof.address);

        // 케어 요청이 있으면 그 지역을 우선 사용
        const { data: req } = await supabase.from('patient_care_requests')
          .select('care_region')
          .eq('user_id', auth.userId)
          .eq('status', 'pending')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (req?.care_region) setPatientRegion(req.care_region);

        // 환자 프로필에서 선호 간병인 성별 가져오기
        const { data: pp } = await supabase.from('patient_profiles')
          .select('preferred_caregiver_gender')
          .eq('user_id', auth.userId)
          .maybeSingle();
        if (pp?.preferred_caregiver_gender && pp.preferred_caregiver_gender !== '무관') {
          setPreferredGender(pp.preferred_caregiver_gender);
        }
      } catch { /* 조용히 실패 - 추천/정렬에만 영향 */ }
    }
    loadPatientPrefs();
  }, [auth.userId]);

  async function loadCaregivers() {
    setDataLoading(true);
    setDataError('');
    try {
      const { data: cgList, error: cgErr } = await supabase
        .from('caregiver_profiles')
        .select('*')
        .eq('is_active', true);

      if (cgErr) throw cgErr;
      if (!cgList || cgList.length === 0) {
        setAllCaregivers([]);
        setDataLoading(false);
        return;
      }

      const userIds = cgList.map((cg) => cg.user_id);

      const { data: profiles, error: profileErr } = await supabase
        .from('profiles')
        .select('id, name, gender, birth_date, avatar_url, latitude, longitude')
        .in('id', userIds);

      if (profileErr) throw profileErr;

      const profileMap = new Map((profiles || []).map((p) => [p.id, p]));

      const mapped: CaregiverItem[] = cgList.map((cg) => {
        const profile = profileMap.get(cg.user_id);
        const age = profile?.birth_date
          ? new Date().getFullYear() - new Date(profile.birth_date).getFullYear()
          : 0;

        return {
          userId: cg.user_id,
          name: profile?.name || '이름 없음',
          gender: profile?.gender || '',
          age,
          avatarUrl: profile?.avatar_url || null,
          experienceYears: cg.experience_years || 0,
          hourlyRate: cg.hourly_rate || 15000,
          availableRegions: cg.available_regions || [],
          certificates: cg.certificates || [],
          introduction: cg.introduction || '',
          availableDays: cg.available_days || [],
          availableTimes: cg.available_times || [],
          ratingAvg: Number(cg.rating_avg) || 0,
          totalMatches: cg.total_matches || 0,
          earliestStartDate: cg.earliest_start_date || null,
          latitude: Number(profile?.latitude) || 0,
          longitude: Number(profile?.longitude) || 0,
          distance: 0,
        };
      });

      setAllCaregivers(mapped);
    } catch (err) {
      console.error('Failed to load caregivers:', err);
      setDataError('간병인 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setDataLoading(false);
    }
  }

  const filtered = useMemo(() => {
    let results = allCaregivers.filter((cg) => {
      if (selectedRegions.length > 0) {
        const hasOverlap = selectedRegions.some((r) => cg.availableRegions.includes(r));
        if (!hasOverlap) return false;
      }

      if (searchText.trim()) {
        const keyword = searchText.trim().toLowerCase();
        const matchRegion = cg.availableRegions.some((r) => r.toLowerCase().includes(keyword));
        const matchName = cg.name.toLowerCase().includes(keyword);
        const matchIntro = cg.introduction.toLowerCase().includes(keyword);
        if (!matchRegion && !matchName && !matchIntro) return false;
      }

      if (cg.experienceYears < minExp) return false;
      if (cg.hourlyRate > maxRate) return false;
      if (selectedDays.length > 0 && !selectedDays.every((d) => cg.availableDays.includes(d))) return false;
      if (selectedCerts.length > 0 && !selectedCerts.every((c) => cg.certificates.includes(c))) return false;
      return true;
    });

    results = results.map((cg) => ({
      ...cg,
      distance: getCaregiverDistance(myLocation.lat, myLocation.lng, cg.latitude, cg.longitude, cg.availableRegions),
    }));

    if (sortMode === 'compound') {
      // 복합 정렬: 1순위 거리 → 2순위 선호 성별 매칭 → 3순위 평점
      results.sort((a, b) => {
        // 1순위: 거리 (가까운 순)
        if (a.distance !== b.distance) return a.distance - b.distance;

        // 2순위: 환자 선호 성별과 일치하는 간병인 우선
        const aGenderMatch = preferredGender && a.gender === preferredGender ? 1 : 0;
        const bGenderMatch = preferredGender && b.gender === preferredGender ? 1 : 0;
        if (aGenderMatch !== bGenderMatch) return bGenderMatch - aGenderMatch;

        // 3순위: 평점 높은 순
        return b.ratingAvg - a.ratingAvg;
      });
    } else if (sortMode === 'distance') {
      results.sort((a, b) => a.distance - b.distance);
    } else if (sortMode === 'rating') {
      results.sort((a, b) => b.ratingAvg - a.ratingAvg);
    } else if (sortMode === 'recommend') {
      results.sort((a, b) => {
        const scoreB = calculateRecommendationScore(b);
        const scoreA = calculateRecommendationScore(a);
        return scoreB - scoreA;
      });
    }

    return results;
  }, [allCaregivers, searchText, selectedRegions, minExp, maxRate, selectedDays, selectedCerts, sortMode, myLocation, reviewsData, patientRegion, preferredGender]);

  // AI 추천 점수 계산 및 추천 간병인 선정
  const calculateRecommendationScore = (cg: CaregiverItem): number => {
    let score = 0;

    // 1. 거리 점수 (최대 30점) - 가까울수록 높음
    const distKm = cg.distance / 1000;
    if (distKm <= 2) score += 30;
    else if (distKm <= 5) score += 25;
    else if (distKm <= 10) score += 18;
    else if (distKm <= 20) score += 10;
    else if (distKm <= 30) score += 4;
    else score += 1;

    // 2. 평점 점수 (최대 25점) - 리뷰 기반 평점
    const realReview = reviewsData[cg.userId];
    const rating = realReview && realReview.count > 0 ? realReview.avg : cg.ratingAvg;
    if (rating > 0) score += (rating / 5) * 25;

    // 3. 경력 점수 (최대 15점) - 오래된 경력자 선호
    score += Math.min(cg.experienceYears / 10, 1) * 15;

    // 4. 자격증 점수 (최대 15점) - 자격증 많을수록 신뢰도 ↑
    score += Math.min(cg.certificates.length / 3, 1) * 15;

    // 5. 매칭 신뢰도 (최대 10점) - 많이 매칭될수록 검증된 간병인
    score += Math.min(cg.totalMatches / 15, 1) * 10;

    // 6. 지역 일치 보너스 (최대 5점) - 환자 지역과 일치하면 추가 점수
    if (patientRegion) {
      const pRegion = patientRegion.split(' ')[0]; // 시/도 단위 추출
      const regionMatch = cg.availableRegions.some(
        (r) => r.includes(pRegion) || pRegion.includes(r)
      );
      if (regionMatch) score += 5;
    }

    return score;
  };

  useMemo(() => {
    if (filtered.length < 2) {
      setRecommendedIds(new Set());
      return;
    }

    const scored = filtered.map((cg) => ({
      userId: cg.userId,
      score: calculateRecommendationScore(cg),
    }));

    scored.sort((a, b) => b.score - a.score);

    // 상위 1명을 추천 (점수가 60점 이상일 때만)
    const top = scored[0];
    if (top && top.score >= 60) {
      setRecommendedIds(new Set([top.userId]));
    } else {
      setRecommendedIds(new Set());
    }
  }, [filtered, patientRegion, reviewsData]);

  useEffect(() => {
    if (filtered.length === 0) return;
    const userIds = filtered.map((cg) => cg.userId);
    loadReviewsBatch(userIds);
  }, [filtered]);

  async function loadReviewsBatch(userIds: string[]) {
    try {
      const { data } = await supabase
        .from('reviews')
        .select('reviewee_id, rating')
        .in('reviewee_id', userIds);

      const map: Record<string, { count: number; avg: number; ratings: number[] }> = {};
      (data || []).forEach((r: { reviewee_id: string; rating: number }) => {
        if (!map[r.reviewee_id]) map[r.reviewee_id] = { count: 0, avg: 0, ratings: [] };
        map[r.reviewee_id].count++;
        map[r.reviewee_id].ratings.push(r.rating);
      });

      const result: Record<string, { count: number; avg: number }> = {};
      Object.entries(map).forEach(([uid, val]) => {
        result[uid] = {
          count: val.count,
          avg: Math.round((val.ratings.reduce((a, b) => a + b, 0) / val.ratings.length) * 10) / 10,
        };
      });

      setReviewsData(result);
    } catch (err) {
      console.error('Failed to load reviews:', err);
    }
  }

  function toggle(arr: string[], val: string) {
    return arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];
  }

  function getDaysUntilStart(dateStr: string | null): number {
    if (!dateStr) return 999;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(dateStr);
    target.setHours(0, 0, 0, 0);
    return Math.max(0, Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
  }

  function toggleProvinceExpand(province: string) {
    setExpandedProvinces((prev) =>
      prev.includes(province) ? prev.filter((p) => p !== province) : [...prev, province]
    );
  }

  function toggleDistrict(districtKey: string) {
    setSelectedRegions((prev) =>
      prev.includes(districtKey) ? prev.filter((r) => r !== districtKey) : [...prev, districtKey]
    );
  }

  function getProvinceSelectedCount(province: string): number {
    const districts = REGION_HIERARCHY[province] || [];
    return districts.filter((d) => selectedRegions.includes(`${province} ${d}`)).length;
  }

  function getProvinceTotalCount(province: string): number {
    return (REGION_HIERARCHY[province] || []).length;
  }

  function toggleAllProvinces() {
    if (regionAllExpanded) {
      setExpandedProvinces([]);
      setRegionAllExpanded(false);
    } else {
      setExpandedProvinces([...ALL_PROVINCES]);
      setRegionAllExpanded(true);
    }
  }

  function clearAllRegions() {
    setSelectedRegions([]);
  }

  return (
    <AppLayout>
      <div className="min-h-screen px-5 pt-5 pb-24 bg-gray-50">
        <div className="mb-4">
          <h1 className="text-xl font-bold text-gray-900 mb-3">간병인 찾기</h1>

          <div className="flex gap-2">
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="이름, 지역, 키워드 검색"
                className="w-full pl-9 pr-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#2E7D6B] bg-white"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-3 rounded-xl text-sm font-bold border-2 cursor-pointer transition-all whitespace-nowrap ${
                showFilters ? 'border-[#2E7D6B] text-[#2E7D6B] bg-[#f0f9f6]' : 'border-gray-200 text-gray-500 bg-white'
              }`}
            >
              <i className="ri-filter-3-line mr-1" />
              필터
            </button>
          </div>
        </div>

        {/* 지역 선택 */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <i className="ri-map-pin-line text-[#2E7D6B]" />
              <span className="text-sm font-bold text-gray-800">지역 선택</span>
              {selectedRegions.length > 0 && (
                <span className="px-1.5 py-0.5 rounded-md bg-[#f0f9f6] text-[10px] font-bold text-[#2E7D6B]">
                  {selectedRegions.length}곳
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selectedRegions.length > 0 && (
                <button onClick={clearAllRegions} className="text-[10px] text-gray-400 underline cursor-pointer hover:text-gray-600 whitespace-nowrap">
                  전체해제
                </button>
              )}
              <button onClick={toggleAllProvinces} className="text-[10px] px-2 py-1 rounded-lg bg-gray-100 text-gray-500 cursor-pointer hover:bg-gray-200 whitespace-nowrap">
                {regionAllExpanded ? '모두 접기' : '모두 펼치기'}
              </button>
            </div>
          </div>

          {selectedRegions.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {selectedRegions.map((region) => (
                <span key={region} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#f0f9f6] border border-[#a8e0c8] text-xs text-[#2E7D6B] font-medium">
                  {region}
                  <button onClick={() => setSelectedRegions((prev) => prev.filter((r) => r !== region))} className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-[#d4f2e3] cursor-pointer">
                    <i className="ri-close-line text-[10px]" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="space-y-1.5 max-h-[360px] overflow-y-auto pr-0.5">
            {ALL_PROVINCES.map((province) => {
              const isExpanded = expandedProvinces.includes(province);
              const selectedCount = getProvinceSelectedCount(province);
              const totalCount = getProvinceTotalCount(province);
              const districts = REGION_HIERARCHY[province] || [];

              return (
                <div key={province} className="rounded-xl border border-gray-100 overflow-hidden">
                  <button
                    onClick={() => toggleProvinceExpand(province)}
                    className={`w-full flex items-center justify-between px-3.5 py-2.5 cursor-pointer transition-colors ${
                      isExpanded ? 'bg-[#f0f9f6]' : 'bg-white hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-semibold ${selectedCount > 0 ? 'text-[#2E7D6B]' : 'text-gray-700'}`}>
                        {province}
                      </span>
                      {selectedCount > 0 && (
                        <span className="px-1.5 py-0.5 rounded-md bg-[#2E7D6B] text-[10px] font-bold text-white">
                          {selectedCount}/{totalCount}
                        </span>
                      )}
                    </div>
                    <i className={`ri-arrow-down-s-line text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                  </button>

                  {isExpanded && (
                    <div className="px-3.5 py-3 bg-gray-50/50 border-t border-gray-100">
                      <div className="flex flex-wrap gap-1.5">
                        {districts.map((district) => {
                          const regionKey = `${province} ${district}`;
                          const isSelected = selectedRegions.includes(regionKey);
                          return (
                            <button
                              key={regionKey}
                              onClick={() => toggleDistrict(regionKey)}
                              className={`px-2.5 py-1.5 rounded-lg border text-xs cursor-pointer transition-all whitespace-nowrap ${
                                isSelected
                                  ? 'border-[#2E7D6B] text-[#2E7D6B] bg-[#f0f9f6] font-medium'
                                  : 'border-gray-200 text-gray-500 bg-white hover:border-gray-300'
                              }`}
                            >
                              {district}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* 내 위치 설정 */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4">
          <div className="flex items-center gap-2 mb-2.5">
            <i className="ri-map-pin-line text-[#2E7D6B]" />
            <span className="text-sm font-bold text-gray-800">내 위치 설정</span>
            <span className="text-[11px] text-gray-400">기준: {myLocation.label}</span>
          </div>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={locationInput}
                onChange={(e) => { setLocationInput(e.target.value); setLocationError(''); }}
                onFocus={() => setLocationFocused(true)}
                onBlur={() => setTimeout(() => setLocationFocused(false), 150)}
                onKeyDown={(e) => { if (e.key === 'Enter') applyLocationInput(); }}
                placeholder="예: 서울 강남구"
                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#2E7D6B] bg-white"
              />
              {locationFocused && locationInput.trim() && locationSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-md z-20 overflow-hidden">
                  {locationSuggestions.map((s) => (
                    <button
                      key={s.key}
                      onClick={() => selectLocationSuggestion(s)}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-[#f0f9f6] cursor-pointer flex items-center gap-2 whitespace-nowrap"
                    >
                      <i className="ri-map-pin-line text-xs text-[#2E7D6B]" />
                      {s.key}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={locateMe}
              disabled={locating}
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-xs font-bold text-gray-600 cursor-pointer hover:border-[#2E7D6B] hover:text-[#2E7D6B] transition-colors whitespace-nowrap disabled:opacity-50"
            >
              <i className={`${locating ? 'ri-loader-4-line animate-spin' : 'ri-crosshair-line'} text-sm`} />
              {locating ? '확인 중' : '내 위치'}
            </button>
            <button
              onClick={applyLocationInput}
              className="px-3 py-2.5 rounded-xl bg-[#2E7D6B] text-white text-xs font-bold cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap"
            >
              설정
            </button>
          </div>
          {locationError && (
            <p className="text-[11px] text-red-400 mt-1.5">{locationError}</p>
          )}
        </div>

        {/* 정렬 */}
        <div className="flex items-center gap-2 mb-4 overflow-x-auto">
          <span className="text-[10px] text-gray-400 shrink-0 whitespace-nowrap">정렬:</span>
          {[
            { key: 'compound' as SortMode, label: '종합순', icon: 'ri-sort-desc' },
            { key: 'distance' as SortMode, label: '거리순', icon: 'ri-map-pin-line' },
            { key: 'rating' as SortMode, label: '평점순', icon: 'ri-star-line' },
            { key: 'recommend' as SortMode, label: 'AI 추천', icon: 'ri-sparkling-2-fill' },
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => setSortMode(opt.key)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap cursor-pointer border transition-all shrink-0 ${
                sortMode === opt.key
                  ? 'bg-[#2E7D6B] text-white border-[#2E7D6B]'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
              }`}
            >
              <i className={opt.icon} />
              {opt.label}
            </button>
          ))}
        </div>

        {/* 상세 필터 */}
        {showFilters && (
          <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">최소 경력: {minExp}년 이상</label>
              <input type="range" min={0} max={20} value={minExp} onChange={(e) => setMinExp(parseInt(e.target.value))} className="w-full accent-[#2E7D6B]" />
              <div className="flex justify-between text-[10px] text-gray-400 mt-1"><span>신입</span><span>20년</span></div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">최대 시급: {maxRate.toLocaleString()}원</label>
              <input type="range" min={10000} max={50000} step={1000} value={maxRate} onChange={(e) => setMaxRate(parseInt(e.target.value))} className="w-full accent-[#2E7D6B]" />
              <div className="flex justify-between text-[10px] text-gray-400 mt-1"><span>1만원</span><span>5만원</span></div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">근무 가능 요일</label>
              <div className="flex gap-2">
                {dayOptions.map((d) => (
                  <button key={d} onClick={() => setSelectedDays((prev) => toggle(prev, d))}
                    className={`flex-1 py-2 rounded-lg text-xs font-bold border-2 cursor-pointer transition-all ${
                      selectedDays.includes(d) ? 'border-[#2E7D6B] bg-[#f0f9f6] text-[#2E7D6B]' : 'border-gray-100 bg-white text-gray-400'
                    }`}>{d}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">필수 자격증</label>
              <div className="flex flex-wrap gap-2">
                {certificateOptions.map((c) => (
                  <button key={c} onClick={() => setSelectedCerts((prev) => toggle(prev, c))}
                    className={`px-3 py-2 rounded-xl text-xs font-medium border-2 cursor-pointer transition-all whitespace-nowrap ${
                      selectedCerts.includes(c) ? 'border-[#1976D2] bg-blue-50 text-[#1976D2]' : 'border-gray-100 bg-white text-gray-500 hover:border-gray-200'
                    }`}>{c}</button>
                ))}
              </div>
            </div>
            <button onClick={() => { setMinExp(0); setMaxRate(50000); setSelectedDays([]); setSelectedCerts([]); setSearchText(''); }}
              className="w-full py-2.5 rounded-xl text-xs font-medium text-gray-500 border border-gray-200 cursor-pointer hover:bg-gray-50 whitespace-nowrap">
              상세 필터 초기화
            </button>
          </div>
        )}

        {/* 로딩 */}
        {dataLoading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-[#2E7D6B] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : dataError ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-3">
              <i className="ri-error-warning-line text-xl text-red-400" />
            </div>
            <p className="text-sm text-gray-500">{dataError}</p>
            <button onClick={loadCaregivers} className="mt-3 px-4 py-2 rounded-xl text-xs font-medium text-[#2E7D6B] bg-[#f0f9f6] cursor-pointer whitespace-nowrap">
              다시 시도
            </button>
          </div>
        ) : (
          <>
            <p className="text-xs text-gray-500 mb-3">
              {filtered.length}명의 간병인을 찾았어요
              {(() => {
                const excellentCount = filtered.filter((cg) => cg.totalMatches >= 10 && cg.ratingAvg >= 4.8).length;
                return excellentCount > 0 ? (
                  <span className="ml-1.5 inline-flex items-center gap-0.5 text-emerald-600 font-medium">
                    <i className="ri-verified-badge-fill text-[11px]" />
                    우수간병인 {excellentCount}명
                  </span>
                ) : null;
              })()}
              {recommendedIds.size > 0 && (
                <span className="ml-1.5 inline-flex items-center gap-0.5 text-amber-600 font-medium">
                  <i className="ri-sparkling-2-fill text-[11px]" />
                  AI 추천: 거리 · 평점 · 경력 · 자격증 · 지역 종합 분석
                </span>
              )}
            </p>

            <div className="space-y-3">
              {filtered.map((cg) => {
                const daysUntil = getDaysUntilStart(cg.earliestStartDate);
                const isRecommended = recommendedIds.has(cg.userId);
                const isExcellent = cg.totalMatches >= 10 && cg.ratingAvg >= 4.8;
                return (
                  <button
                    key={cg.userId}
                    onClick={() => navigate(`/app/caregiver/${cg.userId}`)}
                    className="w-full text-left bg-white rounded-2xl border border-gray-100 p-4 cursor-pointer hover:shadow-sm transition-shadow relative overflow-hidden"
                  >
                    {isRecommended && (
                      <div className="absolute top-0 left-0 z-10">
                        <div className="flex items-center gap-1 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-[10px] font-extrabold px-2.5 py-1 rounded-tl-2xl rounded-br-xl shadow-sm">
                          <i className="ri-sparkling-2-fill text-[11px]" />
                          AI 추천
                        </div>
                      </div>
                    )}
                    {isExcellent && (
                      <div className="absolute top-0 right-0 z-10">
                        <div className="flex items-center gap-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[10px] font-extrabold px-2.5 py-1 rounded-tr-2xl rounded-bl-xl shadow-sm">
                          <i className="ri-verified-badge-fill text-[11px]" />
                          우수간병인
                        </div>
                      </div>
                    )}
                    <div className="flex gap-3">
                      {cg.avatarUrl ? (
                        <img src={cg.avatarUrl} alt={cg.name} className="w-14 h-14 rounded-xl object-cover shrink-0" />
                      ) : (
                        <div className="w-14 h-14 rounded-xl bg-[#E8F5E9] flex items-center justify-center shrink-0">
                          <i className="ri-user-3-line text-xl text-[#2E7D6B]/40" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h3 className="text-sm font-bold text-gray-900">{cg.name}</h3>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-500 font-medium">
                            {cg.gender} · {cg.age}세
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleBookmark(cg.userId); }}
                            className="ml-auto w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer transition-all"
                          >
                            <i className={`${isBookmarked(cg.userId) ? 'ri-bookmark-fill text-amber-500' : 'ri-bookmark-line text-gray-300 hover:text-amber-400'} text-base`} />
                          </button>
                        </div>
                        <p className="text-xs text-[#2E7D6B] font-semibold mb-1">
                          {cg.hourlyRate.toLocaleString()}원/시간 · 경력 {cg.experienceYears}년
                        </p>
                        <div className="flex flex-wrap gap-1 mb-1.5">
                          {cg.availableRegions.slice(0, 4).map((r) => (
                            <span key={r} className="text-[10px] px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-500">{r}</span>
                          ))}
                          {cg.availableRegions.length > 4 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-400">+{cg.availableRegions.length - 4}</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 line-clamp-1 mb-2">{cg.introduction}</p>
                        <div className="flex flex-wrap gap-1">
                          {cg.certificates.slice(0, 3).map((cert) => (
                            <span key={cert} className="text-[10px] px-2 py-0.5 rounded-full bg-[#f0f9f6] text-[#2E7D6B] font-medium">{cert}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-0.5 text-xs text-gray-600">
                          <i className="ri-map-pin-line text-[#2E7D6B]" />
                          <span className="font-bold">{formatDistance(cg.distance)}</span>
                        </span>
                        <span className="flex items-center gap-1 text-xs">
                          <span className="text-gray-400">시작 가능:</span>
                          <span className={`font-bold ${daysUntil <= 3 && daysUntil >= 0 ? 'text-[#F57C00]' : 'text-gray-600'}`}>
                            {daysUntil === 0 ? '내일부터' : daysUntil < 999 ? `${daysUntil}일 후` : '협의'}
                          </span>
                        </span>
                        {(() => {
                          const realReview = reviewsData[cg.userId];
                          if (realReview && realReview.count > 0) {
                            return (
                              <span className="flex items-center gap-0.5 text-xs">
                                <i className="ri-star-fill text-amber-400" />
                                <span className="font-bold text-gray-900">{realReview.avg}</span>
                                <span className="text-gray-400">({realReview.count})</span>
                              </span>
                            );
                          }
                          if (cg.ratingAvg > 0) {
                            return (
                              <span className="flex items-center gap-0.5 text-xs">
                                <i className="ri-star-fill text-amber-400" />
                                <span className="font-bold text-gray-900">{cg.ratingAvg.toFixed(1)}</span>
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      <span className="text-xs text-[#2E7D6B] font-medium flex items-center gap-1">
                        프로필 보기 <i className="ri-arrow-right-s-line" />
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            {filtered.length === 0 && !dataLoading && (
              <div className="text-center py-16">
                <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-search-line text-xl text-gray-400" />
                </div>
                <p className="text-sm text-gray-500">조건에 맞는 간병인이 없어요</p>
                <p className="text-xs text-gray-400 mt-1">지역이나 필터를 조정해보세요</p>
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}